import React, { createContext, useContext, useState, useCallback } from 'react';

const STORAGE_KEY = 'securecoop_notifications';

const defaultNotifications = [
  { id: '1', type: 'success', messageKey: 'notifSimulationComplete', message: 'Simulation completed', time: Date.now() - 600000, read: false },
  { id: '2', type: 'warning', messageKey: 'notifPhishingClicked', message: 'User clicked the phishing link', time: Date.now() - 120000, read: false },
  { id: '3', type: 'info', messageKey: 'notifSEDataShared', message: 'Sensitive data shared in social engineering simulation', time: Date.now() - 3600000, read: true },
  { id: '4', type: 'warning', messageKey: 'notifVulnerabilityDetected', message: 'New vulnerability detected', time: Date.now() - 7200000, read: false },
  { id: '5', type: 'info', messageKey: 'notifRiskUpdated', message: 'Security score updated', time: Date.now() - 86400000, read: true },
];

function loadNotifications() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : defaultNotifications;
    }
  } catch (_) {}
  return defaultNotifications;
}

function saveNotifications(notifications) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(notifications));
}

const NotificationsContext = createContext(null);

export function NotificationsProvider({ children }) {
  const [notifications, setNotifications] = useState(loadNotifications);

  const markAsRead = useCallback((id) => {
    setNotifications((prev) => {
      const next = prev.map((n) => (n.id === id ? { ...n, read: true } : n));
      saveNotifications(next);
      return next;
    });
  }, []);

  const markAllAsRead = useCallback(() => {
    setNotifications((prev) => {
      const next = prev.map((n) => ({ ...n, read: true }));
      saveNotifications(next);
      return next;
    });
  }, []);

  const addNotification = useCallback(({ type = 'info', message }) => {
    const item = {
      id: String(Date.now()),
      type,
      message,
      time: Date.now(),
      read: false,
    };
    setNotifications((prev) => {
      const next = [item, ...prev].slice(0, 20);
      saveNotifications(next);
      return next;
    });
  }, []);

  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <NotificationsContext.Provider
      value={{
        notifications,
        unreadCount,
        markAsRead,
        markAllAsRead,
        addNotification,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationsContext);
  return ctx || {
    notifications: [],
    unreadCount: 0,
    markAsRead: () => {},
    markAllAsRead: () => {},
    addNotification: () => {},
  };
}
